/**
 * 知识抽查系统 - 权限检查工具函数
 */
import knowledgeLotteryApi from '../services/knowledgeLotteryApi';
import { hasPrivateFundPermission } from './privateFundAuth';

/**
 * 检查用户是否有知识管理权限
 * 系统管理员和知识管理员都有此权限
 */
export const hasKnowledgeManagementPermission = async () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      console.log('权限检查：未找到用户信息');
      return false;
    }

    const user = JSON.parse(userStr);
    console.log('权限检查：当前用户信息', { 
      id: user.id || user._id, 
      email: user.email
    });

    // 检查用户是否有知识管理角色（严格按角色权限控制）
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('权限检查：未找到token');
        return false;
      }
      
      const response = await knowledgeLotteryApi.userRoleApi.getUserRoleByUserId(user._id || user.id);
      console.log('权限检查：用户角色API响应', response);
      
      if (response.success && response.data && response.data.roleId) {
        const role = response.data.roleId;
        console.log('权限检查：用户角色', { roleName: role.name, isActive: role.isActive, permissions: role.permissions });
        
        // 检查角色是否激活
        if (!role.isActive) {
          console.log('权限检查：角色未激活');
          return false;
        }
        
        // 系统管理员有所有权限（更健壮的检查，处理可能的空格或编码问题）
        const roleName = String(role.name || '').trim();
        const isSystemAdmin = roleName === '系统管理员' || roleName.includes('系统管理员');
        console.log('权限检查：是否为系统管理员', { 
          roleName, 
          originalName: role.name,
          roleNameType: typeof role.name,
          isSystemAdmin,
          comparison1: roleName === '系统管理员',
          comparison2: role.name === '系统管理员'
        });
        
        if (isSystemAdmin) {
          console.log('权限检查：系统管理员，直接通过');
          return true;
        }
        
        // 检查是否有知识管理权限（至少有一个资源的view权限即可进入知识抽查系统）
        // 普通用户只有view权限也可以进入，但不能进入管理页面
        const permissions = role.permissions || {};
        const knowledgeResources = ['knowledgeContent', 'quizQuestion', 'session', 'member', 'category', 'group'];
        const hasPermission = knowledgeResources.some(resource => {
          const resourcePermissions = permissions[resource];
          if (!resourcePermissions) return false;
          // 只要有view权限就可以进入知识抽查系统
          const hasView = resourcePermissions.view === true;
          console.log(`权限检查：资源 ${resource}`, { hasView });
          return hasView;
        });
        
        console.log('权限检查：最终权限结果', hasPermission);
        return hasPermission;
      } else {
        console.log('权限检查：用户没有分配角色');
        return false;
      }
    } catch (error) {
      // 如果API调用失败（比如用户未登录或没有角色），返回false但不抛出错误
      console.error('检查用户角色失败:', error);
      return false;
    }

    return false;
  } catch (error) {
    // 捕获所有错误，避免影响页面渲染
    console.error('检查知识管理权限失败:', error);
    return false;
  }
};

/**
 * 检查用户是否是系统管理员
 * 只有系统管理员可以查看用户行为统计
 */
export const isSystemAdmin = async () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      return false;
    }

    const user = JSON.parse(userStr);
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }
    
    try {
      const response = await knowledgeLotteryApi.userRoleApi.getUserRoleByUserId(user._id || user.id);
      
      if (response.success && response.data && response.data.roleId) {
        const role = response.data.roleId;
        
        // 检查角色是否激活
        if (!role.isActive) {
          return false;
        }
        
        // 检查是否是系统管理员
        const roleName = String(role.name || '').trim();
        const isAdmin = roleName === '系统管理员' || roleName.includes('系统管理员');
        
        return isAdmin;
      }
      
      return false;
    } catch (error) {
      console.error('检查系统管理员权限失败:', error);
      return false;
    }
  } catch (error) {
    console.error('检查系统管理员权限失败:', error);
    return false;
  }
};

/**
 * 检查用户是否有知识管理权限（用于显示管理后台按钮）
 * 需要至少有一个资源的 create 或 update 权限
 */
export const hasKnowledgeManagementAccess = async () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      console.log('管理权限检查：未找到用户信息');
      return false;
    }

    const user = JSON.parse(userStr);
    console.log('管理权限检查：当前用户信息', { 
      id: user.id || user._id, 
      email: user.email
    });

    // 检查用户是否有知识管理角色（严格按角色权限控制）
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('管理权限检查：未找到token');
        return false;
      }
      
      const response = await knowledgeLotteryApi.userRoleApi.getUserRoleByUserId(user._id || user.id);
      console.log('管理权限检查：用户角色API响应', response);
      
      if (response.success && response.data && response.data.roleId) {
        const role = response.data.roleId;
        console.log('管理权限检查：用户角色', { roleName: role.name, isActive: role.isActive, permissions: role.permissions });
        
        // 检查角色是否激活
        if (!role.isActive) {
          console.log('管理权限检查：角色未激活');
          return false;
        }
        
        // 系统管理员有所有权限（更健壮的检查，处理可能的空格或编码问题）
        const roleName = String(role.name || '').trim();
        const isSystemAdmin = roleName === '系统管理员' || roleName.includes('系统管理员');
        console.log('管理权限检查：角色名称检查', { 
          roleName, 
          originalName: role.name,
          isSystemAdmin,
          comparison1: roleName === '系统管理员',
          comparison2: role.name === '系统管理员'
        });
        
        if (isSystemAdmin) {
          console.log('管理权限检查：系统管理员，直接通过');
          return true;
        }
        
        // 检查是否有知识管理权限（至少有一个资源的create或update权限）
        // 知识管理员、个人产品管理员都有管理权限
        const permissions = role.permissions || {};
        const knowledgeResources = ['knowledgeContent', 'quizQuestion', 'session', 'member', 'category', 'group'];
        const hasPermission = knowledgeResources.some(resource => {
          const resourcePermissions = permissions[resource];
          if (!resourcePermissions) return false;
          // 检查是否有create或update权限（管理权限）
          const hasCreate = resourcePermissions.create === true;
          const hasUpdate = resourcePermissions.update === true;
          const hasManagePermission = hasCreate || hasUpdate;
          console.log(`管理权限检查：资源 ${resource}`, { hasCreate, hasUpdate, hasManagePermission });
          return hasManagePermission;
        });
        
        console.log('管理权限检查：最终权限结果', hasPermission);
        return hasPermission;
      } else {
        console.log('管理权限检查：用户没有分配角色');
        return false;
      }
    } catch (error) {
      // 如果API调用失败（比如用户未登录或没有角色），返回false但不抛出错误
      console.error('检查用户角色失败:', error);
      return false;
    }

    return false;
  } catch (error) {
    // 捕获所有错误，避免影响页面渲染
    console.error('检查知识管理权限失败:', error);
    return false;
  }
};

/**
 * 检查用户是否有查看权限（同步版本，基于本地存储的用户信息）
 * 用于快速检查，不需要API调用
 */
export const hasViewPermission = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      return false;
    }

    const user = JSON.parse(userStr);
    
    // 系统管理员有所有权限
    if (user.isAdmin === true) {
      return true;
    }

    // 其他用户需要登录才能查看
    return true;
  } catch (error) {
    console.error('检查查看权限失败:', error);
    return false;
  }
};
